import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { DeleteMultipleMedia } from '../../attachments/attachments.actions';
import { RemoveAttachments } from '../../points.actions';

import { TAttachment } from '../../attachments/attachments.model';
import { DeleteFilesModalComponent } from './delete-files-modal/delete-files-modal.component';

import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { AttachmentsFilesService } from '../../attachments/attachments-files.service';
import { AttachmentsService } from '../../attachments/attachments.service';
import { PointsService } from '../../points.service';
import { PointActivityService } from '../point-timeline/point-activity.service';
import { PointAttachmentsService } from './point-attachments.service';

import { Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { TDeleteFilesRequest } from 'src/app/project/data-providers/api-providers/files-api-provider/files-requests.model';
import { translate } from 'src/app/project/features/translate/translate';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSite,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { NEW_POINT_ID } from 'src/app/project/shared/constants/point.const';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { EDocumentType } from 'src/app/project/shared/enums/document-type.enum';
import { CustomTableService } from '../../../site/site-table/custom-table/custom-table.service';

const countAttachments = (attachmentIds: TDeleteFilesRequest): number =>
  Object.values(attachmentIds).flat().length;

@Injectable({
  providedIn: 'root',
})
export class PointAttachmentsDeleteService {
  _id: string;
  workspaceId: string;
  full: boolean;

  constructor(
    private store: Store,
    private modalService: ModalService,
    private promptService: PromptService,
    private attachmentsFilesService: AttachmentsFilesService,
    private attachmentsService: AttachmentsService,
    private pointAttachmentsService: PointAttachmentsService,
    private pointActivityService: PointActivityService,
    private pointsService: PointsService,
    private customTableService: CustomTableService,
  ) {}

  deleteAttachments(
    attachments: TAttachment[],
    pointId: string,
    workspaceId: string,
  ): Promise<void> {
    const attachmentIds = this.buildDeleteRequest(attachments);

    logEventInGTAG(EGoogleEventSite.SITE__POINT__ATTACHMENT_DELETE, {
      source: EGoogleEventCategory.SITE,
    });

    return firstValueFrom(
      this.attachmentsFilesService.deletePointFiles(pointId, attachmentIds).pipe(
        tap(() => this.finishDelete(attachmentIds, pointId, workspaceId)),
        catchError((error) => {
          this.showDeleteError(error, countAttachments(attachmentIds));

          return throwError(() => error);
        }),
        map(() => undefined),
      ),
      { defaultValue: undefined },
    );
  }

  private finishDelete(
    attachmentIds: TDeleteFilesRequest,
    pointId: string,
    workspaceId: string,
  ): void {
    const isNewPoint = !pointId || pointId === NEW_POINT_ID;
    const deletedCount = countAttachments(attachmentIds);

    this.store.dispatch(new DeleteMultipleMedia(Object.values(attachmentIds).flat()));
    this.pointAttachmentsService.countSelectedMedia();

    const remaining = this.attachmentsService.getAttachments();
    const containsAttachments =
      Object.keys(remaining.media.attachments).length > 0 ||
      Object.keys(remaining.files.attachments).length > 0;

    this.store.dispatch(new RemoveAttachments({ containsAttachments, _id: pointId, workspaceId }));

    this.promptService.showSuccess(
      translate(deletedCount > 1 ? 'prompt_file_deleted_multiple' : 'prompt_file_deleted'),
    );

    if (isNewPoint) {
      return;
    }

    this.updateTable(pointId, containsAttachments);
    this.pointActivityService.refreshTimeline(workspaceId, pointId);
  }

  deleteAttachment(file: TAttachment): Observable<void> {
    const attachmentIds = this.buildDeleteRequest([file]);

    this.modalService.setData({ _id: this._id, attachmentIds });

    return this.modalService
      .showModal(DeleteFilesModalComponent, {
        blur: !this.full,
        callback: () => {
          this.finishDelete(attachmentIds, this._id, this.workspaceId);
        },
      })
      .pipe(take(1));
  }

  deleteFiles(files: TAttachment[]): void {
    const attachmentIds = this.buildDeleteRequest(files);

    this.modalService.setData({ _id: this._id, attachmentIds });

    this.modalService.showModal(DeleteFilesModalComponent, {
      blur: !this.full,
      callback: () => {
        this.finishDelete(attachmentIds, this._id, this.workspaceId);
      },
    });
  }

  setPoint(_id: string, workspaceId: string, full: boolean): void {
    this._id = _id;
    this.workspaceId = workspaceId;
    this.full = full;
  }

  private buildDeleteRequest(files: TAttachment[]): TDeleteFilesRequest {
    const idsOfType = (type: EDocumentType): string[] =>
      files.filter((file) => file.type === type).map((file) => file.attachmentId);

    return {
      documents: idsOfType(EDocumentType.FILE),
      videos: idsOfType(EDocumentType.VIDEO),
      images: idsOfType(EDocumentType.IMAGE),
      images360: idsOfType(EDocumentType.IMAGE_360),
    };
  }

  private updateTable(pointId: string, containsAttachments: boolean): void {
    if (!containsAttachments) {
      this.customTableService.reloadTable({ preserveTableState: true });
      return;
    }

    const point = this.pointsService.findPoint(pointId);

    if (!point) {
      return;
    }

    this.customTableService.updatePoint({
      _id: pointId,
      field: EBasicField.TITLE,
      newValue: point.title,
      updatedDate: new Date().getTime(),
    });
  }

  private showDeleteError(error: Partial<HttpErrorResponse>, count: number): void {
    if (error.status === EStatusCode.FORBIDDEN) {
      this.promptService.showError(translate('prompt_changes_permission_denied'));
      return;
    }

    this.promptService.showError(
      translate(count > 1 ? 'prompt_file_deleted_multiple_error' : 'prompt_file_deleted_error'),
    );
  }
}
